// ─────────────────────────────────────────────────────────────────────────────
//  filter.renderers.ts
//
//  Pure TemplateResult factory functions for all datatable filter UI.
//  No class, no instance state — every function takes explicit arguments and
//  returns a Lit TemplateResult. The host (UwcDatatable) wires callbacks and
//  reads controller state before calling these functions.
//
//  Exported functions:
//    renderBuiltinFilter   — dispatcher: picks text / multiselect / dropdown
//    renderTextFilter      — text input + match-mode menu
//    renderMultiselectFilter — uwc-dropdown with multiple selection
//    renderDropdownFilter  — uwc-dropdown with single selection
//    renderFilterButton    — overlay trigger used in menu filter-display mode
//    renderFilterRow       — <tr> of filter cells in row filter-display mode
//    renderGlobalFilter    — global search bar (menu mode)
//
// ─────────────────────────────────────────────────────────────────────────────
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import type { TemplateResult } from 'lit';
import {
  FILTER_MODE_LABELS,
  resolveFilterMenuItem,
  type DataTableFilterMeta,
  type FilterMatchMode,
} from './filter.controller.js';
import type { DataTableColumnDef } from './types.js';

// ── Type alias for the per-column filter callback ─────────────────────────────

type FilterCb = (value: unknown, matchMode?: FilterMatchMode) => void;

// ─────────────────────────────────────────────────────────────────────────────
// renderBuiltinFilter — dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function renderBuiltinFilter(
  col: DataTableColumnDef,
  field: string,
  filterVal: unknown,
  matchMode: FilterMatchMode,
  cb: FilterCb,
): TemplateResult {
  if (col.filterMenuType === 'multiselect') return renderMultiselectFilter(col, filterVal, cb);
  if (col.filterMenuType === 'dropdown')    return renderDropdownFilter(col, filterVal, cb);
  return renderTextFilter(col, field, filterVal, matchMode, cb);
}

// ─────────────────────────────────────────────────────────────────────────────
// renderTextFilter — text input + match-mode menu
// ─────────────────────────────────────────────────────────────────────────────

export function renderTextFilter(
  col: DataTableColumnDef,
  _field: string,
  filterVal: unknown,
  matchMode: FilterMatchMode,
  cb: FilterCb,
): TemplateResult {
  const defaultMode = col.filterMatchMode ?? 'contains';
  const isActive    = filterVal != null && filterVal !== '';
  const modeKeys: FilterMatchMode[] = [
    'startsWith', 'contains', 'notContains', 'endsWith', 'equals', 'notEquals',
  ];
  const items = [
    ...modeKeys.map(m => ({
      id:      m,
      label:   FILTER_MODE_LABELS[m],
      command: () => cb(filterVal, m),
    })),
    { separator: true },
    { label: 'No Filter', command: () => cb('', defaultMode) },
  ];
  return html`
    <div class="uwc-dt__filter-input-group">
      <uwc-inputtext
        .value=${String(filterVal ?? '')}
        placeholder=${col.filterPlaceholder ?? 'Search...'}
        fluid
        @uwc-input=${(e: CustomEvent<{ value: string }>) => cb(e.detail.value, matchMode)}
      ></uwc-inputtext>
      <uwc-menu .items=${items} selected-id=${matchMode} placement="bottom-end" offset="4">
        <button
          slot="trigger"
          type="button"
          class=${classMap({ 'uwc-dt__filter-mode-btn': true, 'uwc-dt__filter-mode-btn--active': isActive })}
          title=${FILTER_MODE_LABELS[matchMode] ?? 'Filter mode'}
          aria-label="Filter mode"
        >
          <uwc-icon name="funnel" size="1em" aria-hidden="true"></uwc-icon>
        </button>
      </uwc-menu>
    </div>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// renderMultiselectFilter — uwc-dropdown with multiple selection
// ─────────────────────────────────────────────────────────────────────────────

export function renderMultiselectFilter(
  col: DataTableColumnDef,
  filterVal: unknown,
  cb: FilterCb,
): TemplateResult {
  const items   = (col.filterMenuItems ?? []).map(resolveFilterMenuItem);
  const options = items.map(i => ({ label: i.label, value: i.value }));
  const current = Array.isArray(filterVal) ? filterVal : [];

  return html`
    <uwc-dropdown
      .options=${options}
      .value=${current}
      placeholder=${col.filterPlaceholder ?? 'Select...'}
      fluid
      multiple
      show-clear
      filter
      @uwc-change=${(e: CustomEvent<{ value: unknown }>) => {
        const val = e.detail.value as unknown[];
        cb(val?.length ? val : '', 'in');
      }}
    ></uwc-dropdown>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// renderDropdownFilter — uwc-dropdown with single selection
// ─────────────────────────────────────────────────────────────────────────────

export function renderDropdownFilter(
  col: DataTableColumnDef,
  filterVal: unknown,
  cb: FilterCb,
): TemplateResult {
  const items   = (col.filterMenuItems ?? []).map(resolveFilterMenuItem);
  const options = items.map(i => ({ label: i.label, value: i.value }));
  const current = filterVal !== '' && filterVal != null ? filterVal : null;

  return html`
    <uwc-dropdown
      .options=${options}
      .value=${current}
      placeholder=${col.filterPlaceholder ?? 'Select...'}
      fluid
      show-clear
      @uwc-change=${(e: CustomEvent<{ value: unknown }>) => {
        cb(e.detail.value ?? '', 'equals');
      }}
    ></uwc-dropdown>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// renderFilterButton — overlay trigger used in menu filter-display mode
// ─────────────────────────────────────────────────────────────────────────────

export function renderFilterButton(
  col: DataTableColumnDef,
  isActive: boolean,
  filterVal: unknown,
  matchMode: FilterMatchMode,
  cb: FilterCb,
): TemplateResult {
  const field = col.field ?? '';
  const panel = col.filterElement
    ? col.filterElement({ field, filterValue: filterVal, matchMode, filterCallback: cb })
    : renderBuiltinFilter(col, field, filterVal, matchMode, cb);

  return html`
    <uwc-overlay placement="bottom-start" close-on-outside-click>
      <button
        slot="trigger"
        type="button"
        class=${classMap({
          'uwc-dt__filter-btn': true,
          'uwc-dt__filter-btn--active': isActive,
        })}
        aria-label="Filter ${col.header ?? field}"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <uwc-icon name="funnel" size="0.9em" aria-hidden="true"></uwc-icon>
      </button>
      <div slot="content" class="uwc-dt__filter-panel">
        ${panel}
      </div>
    </uwc-overlay>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// renderFilterRow — <tr> of filter cells for row filter-display mode
// ─────────────────────────────────────────────────────────────────────────────

export function renderFilterRow(
  cols: DataTableColumnDef[],
  filters: DataTableFilterMeta,
  colHasFilter: (col: DataTableColumnDef) => boolean,
  cb: (field: string, value: unknown, matchMode?: FilterMatchMode) => void,
): TemplateResult {
  return html`
    <tr class="uwc-dt__filter-row" part="filter-row" role="row">
      ${cols.map(col => {
        if (!colHasFilter(col)) {
          return html`<th class="uwc-dt__th uwc-dt__th--filter-empty" scope="col" role="columnheader"></th>`;
        }
        const field     = col.field ?? '';
        const meta      = filters[field];
        const filterVal = meta?.value ?? '';
        const matchMode = meta?.matchMode ?? col.filterMatchMode ?? 'contains';
        const colCb: FilterCb = (v, mm) => cb(field, v, mm);

        const content = col.filterElement
          ? col.filterElement({ field, filterValue: filterVal, matchMode, filterCallback: colCb })
          : renderBuiltinFilter(col, field, filterVal, matchMode, colCb);

        return html`
          <th class="uwc-dt__th uwc-dt__th--filter" scope="col" role="columnheader">
            ${content}
          </th>`;
      })}
    </tr>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// renderGlobalFilter — global search bar (menu filter-display mode)
// ─────────────────────────────────────────────────────────────────────────────

export function renderGlobalFilter(
  value: string,
  placeholder: string,
  onInput: (value: string) => void,
): TemplateResult {
  return html`
    <div class="uwc-dt__global-filter">
      <uwc-inputtext
        type="search"
        .value=${value}
        placeholder=${placeholder}
        fluid
        @uwc-input=${(e: CustomEvent<{ value: string }>) => onInput(e.detail.value)}
      >
        <uwc-icon slot="prefix" name="search" size="1.15em" aria-hidden="true"></uwc-icon>
      </uwc-inputtext>
    </div>`;
}
